// Send message form.
import React, { createRef, forwardRef } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import { Drafty } from 'tinode-sdk';

import { KEYPRESS_DELAY, MAX_MESSAGE_LENGTH } from '../config.js';
import ChatEndedBanner from './chat-ended-banner.jsx';
import { filePasted } from '../lib/blob-helpers.js';
import HashNavigation from '../lib/navigation.js';
import { replyFormatter } from '../lib/formatters.js';
import MessageTemplate from './message-template.jsx';
import MessageTemplateIcon from './message-template-icon.jsx';
import PhotoAttachmentIcon from './photo-attachment-icon.jsx';
import SendMessageIcon from './send-message-icon.jsx';
import SvgIcon from './svg-icon.jsx';

const messages = defineMessages({
  type_new_message: {
    id: 'new_message_prompt',
    defaultMessage: 'New message',
    description: 'Prompt in send message field'
  },
  add_image_caption: {
    id: 'image_caption_prompt',
    defaultMessage: 'Image caption',
    description: 'Prompt in SendMessage for attached image'
  },
  file_attachment_too_large: {
    id: 'file_attachment_too_large',
    defaultMessage: 'The file size {size} exceeds the {limit} limit.',
    description: 'Error message when attachment is too large'
  },
  cannot_initiate_upload: {
    id: 'cannot_initiate_file_upload',
    defaultMessage: 'Cannot initiate file upload.',
    description: 'Generic error messagewhen attachment fails'
  },
  icon_title_add_image: {
    id: 'icon_title_add_image',
    defaultMessage: 'Add image',
    description: 'Icon tool tip for attaching an image'
  },
  icon_title_message_templates: {
    id: 'icon_title_message_templates',
    defaultMessage: 'Message templates',
    description: 'Icon tool tip for opening message templates'
  },
  icon_title_send: {
    id: 'icon_title_send',
    defaultMessage: 'Send message',
    description: 'Icon tool tip for sending a message'
  },
  messaging_offline: {
    id: 'messaging_offline_prompt',
    defaultMessage: 'No internet connection.',
    description: 'Prompt when SendMessage is disabled because the browser is offline'
  },
});

const TextAreaWitRef = forwardRef((props, ref) => {
  return <textarea ref={ref} {...props} />;
});

class SendMessage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.textAreaRef = createRef();

    this.state = {
      quote: null,
      message: props.initMessage || '',
      showMessageTemplates: false,
      templatesDismissed: false,
      composeCollapsed: false,
      messageOverflows: false,
    };

    this.singleLineHeight = 0;

    // Timestamp when the previous key press was sent to the server.
    this.keypressTimestamp = 0;

    // Pending requestAnimationFrame handle for deferred height adjustment.
    this.heightAdjustRaf = 0;

    this.handlePasteEvent = this.handlePasteEvent.bind(this);
    this.handleAttachImage = this.handleAttachImage.bind(this);
    this.handleAttachImageClick = this.handleAttachImageClick.bind(this);
    this.handleSend = this.handleSend.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleMessageTyping = this.handleMessageTyping.bind(this);

    this.handleQuoteClick = this.handleQuoteClick.bind(this);

    this.formatReply = this.formatReply.bind(this);

    this.setMessage = this.setMessage.bind(this);
    this.handleToggleMessageTemplates = this.handleToggleMessageTemplates.bind(this);
    this.handleCollapseCompose = this.handleCollapseCompose.bind(this);
    this.handleComposeFocus = this.handleComposeFocus.bind(this);
    this.adjustTextareaHeight = this.adjustTextareaHeight.bind(this);
    this.scheduleTextareaHeightAdjust = this.scheduleTextareaHeightAdjust.bind(this);
  }

  // Defer height adjustment to the next frame so the typed character can paint
  // first. Reading scrollHeight forces a synchronous reflow; doing it on the
  // keystroke path blocks paint and causes visible input lag on mobile.
  scheduleTextareaHeightAdjust() {
    if (this.heightAdjustRaf) {
      return;
    }
    this.heightAdjustRaf = globalThis.requestAnimationFrame(() => {
      this.heightAdjustRaf = 0;
      this.adjustTextareaHeight();
    });
  }

  measureSingleLineHeight() {
    const el = this.textAreaRef.current;
    if (!el) {
      return 0;
    }

    const style = globalThis.getComputedStyle(el);
    const lineHeight = Number.parseFloat(style.lineHeight) || 24;
    const paddingTop = Number.parseFloat(style.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(style.paddingBottom) || 0;
    return Math.ceil(lineHeight + paddingTop + paddingBottom);
  }

  adjustTextareaHeight(updateOverflow = true) {
    const el = this.textAreaRef.current;
    if (!el) {
      return;
    }

    if (!this.singleLineHeight) {
      this.singleLineHeight = this.measureSingleLineHeight();
    }

    const singleLineHeight = this.singleLineHeight;

    el.style.height = 'auto';
    const scrollHeight = el.scrollHeight;
    const overflows = scrollHeight > singleLineHeight + 1;
    el.style.height = `${Math.max(scrollHeight, singleLineHeight)}px`;

    if (!updateOverflow) {
      return;
    }

    if (overflows !== this.state.messageOverflows) {
      this.setState({ messageOverflows: overflows }, () => {
        // Icons showing/hiding changes textarea width; re-sync height only.
        requestAnimationFrame(() => this.adjustTextareaHeight(false));
      });
    }
  }

  componentDidMount() {
    if (this.textAreaRef.current) {
      this.textAreaRef.current.addEventListener('paste', this.handlePasteEvent, false);
      if (window.getComputedStyle(this.textAreaRef.current).getPropertyValue('transition-property') == 'all') {
        // Set focus on desktop, but not on mobile: focus causes soft keyboard to pop up.
        this.textAreaRef.current.focus();
      }
      this.singleLineHeight = this.measureSingleLineHeight();
      this.adjustTextareaHeight();
    }

    this.setState({quote: this.formatReply()});
  }

  componentWillUnmount() {
    if (this.textAreaRef.current) {
      this.textAreaRef.current.removeEventListener('paste', this.handlePasteEvent, false);
    }
    if (this.heightAdjustRaf) {
      globalThis.cancelAnimationFrame(this.heightAdjustRaf);
      this.heightAdjustRaf = 0;
    }
  }

  componentDidUpdate(prevProps) {
    if (this.textAreaRef.current) {
      if (window.getComputedStyle(this.textAreaRef.current).getPropertyValue('transition-property') == 'all') {
        // Set focus on desktop, but not on mobile: focus causes soft keyboard to pop up.
        this.textAreaRef.current.focus();
      }
    }

    if (prevProps.topicName != this.props.topicName) {
      this.setState(
        { message: this.props.initMessage || '', quote: null, messageOverflows: false },
        this.adjustTextareaHeight
      );
    } else if (prevProps.initMessage != this.props.initMessage) {
      const msg = this.props.initMessage || '';
      this.setState({ message: msg, messageOverflows: false }, _ => {
        const el = this.textAreaRef.current;
        if (!el) {
          return;
        }
        // If there is text, scroll to bottom and set caret to the end.
        el.scrollTop = el.scrollHeight;
        el.setSelectionRange(msg.length, msg.length);
        this.adjustTextareaHeight();
      });
    }
    if (prevProps.reply != this.props.reply) {
      this.setState({ quote: this.formatReply() });
    }
  }

  formatReply() {
    return this.props.reply ?
      Drafty.format(this.props.reply.content, replyFormatter, {
        formatMessage: this.props.intl.formatMessage.bind(this.props.intl),
        authorizeURL: this.props.tinode.authorizeURL.bind(this.props.tinode)
      }) : null;
  }

  handlePasteEvent(e) {
    if (this.props.chatEnded || this.props.liveConnection === false) {
      return;
    }
    // Image attachments are disabled (e.g. via feature flag): ignore pasted files.
    if (!this.props.onAttachImage) {
      return;
    }
    // Only image pastes are accepted; non-image files are ignored.
    if (filePasted(e,
      file => { this.props.onAttachImage(file, this.state.message); },
      _ => { /* Non-image attachments disabled. */ },
      this.props.onError)) {

      // If a file was pasted, don't paste base64 data into input field.
      e.preventDefault();
    }
  }

  // Opens the image file picker. Warms up the NSFW model first (no-op when
  // disabled) so it can download while the OS file dialog is open, hiding the
  // model-load latency from the first attachment.
  handleAttachImageClick(e) {
    e.preventDefault();
    if (this.props.onWarmNSFW) {
      this.props.onWarmNSFW();
    }
    this.attachImage.click();
  }

  handleAttachImage(e) {
    if (this.props.liveConnection === false) {
      e.target.value = '';
      return;
    }
    if (e.target.files && e.target.files.length > 0) {
      this.props.onAttachImage(e.target.files[0], this.state.message);
    }
    // Clear the value so the same file can be uploaded again.
    e.target.value = '';
  }

  handleSend(e) {
    e.preventDefault();
    if (this.props.liveConnection === false) {
      return;
    }
    // Preserve spaces in real text (e.g. "hello   "), but block send if only whitespace.
    const message = this.state.message;
    // Send button is disabled over the limit; do not send via click.
    if (message.length > MAX_MESSAGE_LENGTH) {
      return;
    }
    if (message.trim() || this.props.acceptBlank || this.props.noInput) {
      this.props.onSendMessage(message);
      this.setState({
        message: '',
        templatesDismissed: false,
        showMessageTemplates: false,
        composeCollapsed: false,
        messageOverflows: false,
      }, this.adjustTextareaHeight);
    }
  }

  /* Send on Enter key */
  handleKeyPress(e) {
    // Remove this if you don't want Enter to trigger send
    if (e.key === 'Enter') {
      // Have Shift-Enter insert a line break instead
      if (!e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();

        const message = this.state.message;
        // Over-limit: Enter still "sends" so the parent can show a local failed bubble.
        if (message.length > MAX_MESSAGE_LENGTH) {
          if (message.trim() || this.props.acceptBlank || this.props.noInput) {
            this.props.onSendMessage(message);
            this.setState({
              message: '',
              templatesDismissed: false,
              showMessageTemplates: false,
              composeCollapsed: false,
              messageOverflows: false,
            }, this.adjustTextareaHeight);
          }
          return;
        }

        this.handleSend(e);
      }
    }
  }

  handleMessageTyping(e) {
    const message = e.target.value;
    this.setState({
      message,
      ...(message.length === 0 ?
        { templatesDismissed: false, showMessageTemplates: false, composeCollapsed: false, messageOverflows: false } :
        { composeCollapsed: false }),
    }, this.scheduleTextareaHeightAdjust);
    if (this.props.onKeyPress) {
      const now = new Date().getTime();
      if (now - this.keypressTimestamp > KEYPRESS_DELAY) {
        this.props.onKeyPress();
        this.keypressTimestamp = now;
      }
    }
  }

  handleQuoteClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.props.reply && this.props.onQuoteClick) {
      const replyToSeq = this.props.reply.seq;
      this.props.onQuoteClick(replyToSeq);
    }
  }

  setMessage(message) {
    this.setState({
      message: message,
      showMessageTemplates: false,
      templatesDismissed: false,
      composeCollapsed: false,
    }, () => {
      this.adjustTextareaHeight();
      if (this.textAreaRef.current) {
        this.textAreaRef.current.focus();
      }
    });
  }

  handleCollapseCompose(e) {
    e.preventDefault();
    this.setState({ composeCollapsed: true, showMessageTemplates: false }, this.adjustTextareaHeight);
  }

  handleComposeFocus() {
    if (this.state.composeCollapsed) {
      this.setState({ composeCollapsed: false }, this.adjustTextareaHeight);
    }
  }

  handleToggleMessageTemplates(e) {
    e.preventDefault();
    const { message, showMessageTemplates, templatesDismissed } = this.state;
    const isOpen = (message.length === 0 && !templatesDismissed) || showMessageTemplates;

    if (message.length === 0) {
      this.setState({ templatesDismissed: isOpen, showMessageTemplates: false });
    } else {
      this.setState({ showMessageTemplates: !isOpen });
    }
  }

  isMessageTemplatesOpen() {
    const { message, showMessageTemplates, templatesDismissed } = this.state;
    return (message.length === 0 && !templatesDismissed) || showMessageTemplates;
  }

  render() {
    const { formatMessage } = this.props.intl;
    const chatRoomMode = this.props.chatRoomMode;
    const offline = this.props.liveConnection === false;
    const chatEnded = this.props.chatEnded === true;
    const isDark = this.props.isDarkMode;

    if (offline) {
      if (!chatRoomMode) {
        return null;
      }
      return (
        <div id="send-message-wrapper">
          <div id="writing-disabled">
            <div id="archived-chat-note" className={`archived-chat-note archived-chat-note${isDark ? '-dark' : '-light'}`}>
              <div className={`archived-chat-note-heading ${isDark ? 'archived-chat-note-heading-dark' : ''}`}>No Internet Connection detected</div>
              <div className={`archived-chat-note-text ${isDark ? 'archived-chat-note-text-dark' : ''}`}>Please check your internet connection and try again.</div>
            </div>
          </div>
        </div>
      );
    }

    if (chatEnded) {
      if (!chatRoomMode) {
        return null;
      }
      return (
        <div id="send-message-wrapper" className="no-border-top">
          <div id="writing-disabled">
            <ChatEndedBanner isDarkMode={isDark} />
          </div>
        </div>
      );
    }

    const prompt = chatRoomMode ? 'Type here...' : (this.props.messagePrompt ?
      formatMessage(messages[this.props.messagePrompt]) :
      formatMessage(messages.type_new_message));

    const isEditing = !!(this.props.reply && this.props.reply.editing);

    const quote = this.state.quote ?
      (<div id="reply-quote-preview">
        <div className="cancel">
          <a href="#" onClick={e => {e.preventDefault(); this.props.onCancelReply();}}><SvgIcon name="close" viewBox="0 0 19 19" className="icon-close" /></a>
        </div>
        {this.state.quote}
      </div>) : null;

    const hash = HashNavigation.parseUrlHash(window.location.hash);
    const shouldShowMessageTemplate = hash.path.includes('dax') && this.props.messagePrompt !== 'add_image_caption';
    const messageTemplatesOpen = this.isMessageTemplatesOpen();
    const messageTooLong = this.state.message.length > MAX_MESSAGE_LENGTH;
    // Send stays disabled over the limit; Enter can still intentionally fail-send.
    const sendDisabled = messageTooLong;
    const showSendButton = !!this.state.message.trim() || this.props.acceptBlank;
    const hasMessage = this.state.message.length > 0;
    const hasComposeActions = shouldShowMessageTemplate || !!this.props.onAttachImage;
    const overflowsSingleLine = this.state.messageOverflows;
    const isExpanded = hasMessage && overflowsSingleLine && !this.state.composeCollapsed;
    const showComposeActions = !hasMessage || this.state.composeCollapsed || !overflowsSingleLine;
    const showArrowOnly = isExpanded && hasComposeActions;
    const sendBtnClass = 'send-btn send-icon' + (sendDisabled ? ' disabled' : '');

    return (
      <div id="send-message-wrapper">
        {!this.props.noInput ? quote : null}
        <div id="send-message-panel" className={overflowsSingleLine ? 'compose-expanded' : ''}>
          {showArrowOnly ?
            <a
              href="#"
              className="message-template-toggle compose-arrow-toggle"
              onClick={this.handleCollapseCompose}
              title={formatMessage(messages.icon_title_message_templates)}
            >
              <MessageTemplateIcon arrow />
            </a>
            : null}
          {shouldShowMessageTemplate && showComposeActions ?
            <a
              href="#"
              className="message-template-toggle"
              onClick={this.handleToggleMessageTemplates}
              title={formatMessage(messages.icon_title_message_templates)}
              aria-pressed={messageTemplatesOpen}
            >
              <MessageTemplateIcon active={messageTemplatesOpen} />
            </a>
            : null}
          {this.props.onAttachImage && showComposeActions ?
            <a href="#" onClick={this.handleAttachImageClick} title={formatMessage(messages.icon_title_add_image)}>
              <PhotoAttachmentIcon />
            </a>
            :
            null}
          {this.props.noInput ?
            (quote || <div className="hr thin" />) :
            <TextAreaWitRef id="send-message-input" placeholder={prompt}
              rows={1}
              value={this.state.message} onChange={this.handleMessageTyping}
              onFocus={this.handleComposeFocus}
              onKeyDown={this.handleKeyPress}
              ref={this.textAreaRef} />}
          {showSendButton ?
            <a
              className={sendBtnClass}
              href="#"
              onClick={this.handleSend}
              title={formatMessage(messages.icon_title_send)}
              aria-disabled={sendDisabled}
            >
              <span className="send-btn-inner">
                {isEditing ?
                  <i className="material-icons">check_circle</i> :
                  <SendMessageIcon />}
              </span>
            </a>
            : null}
          <input type="file" ref={ref => {this.attachImage = ref}} accept="image/*"
            onChange={this.handleAttachImage} style={{display: 'none'}} />
        </div>
        {shouldShowMessageTemplate && messageTemplatesOpen && !isExpanded && <MessageTemplate
          setMessage={this.setMessage}
          tinode={this.props.tinode}
          topicName={this.props.topicName}
        />}
      </div>
    );
  }
};

export default injectIntl(SendMessage);
